<script setup>
import { ref } from 'vue';
import { Link, router, useForm } from '@inertiajs/vue3';
import ActionMessage from '@/Components/ActionMessage.vue';
import FormSection from '@/Components/FormSection.vue';
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import { toast } from 'vue3-toastify';

const props = defineProps({
    user: Object,
});

const form = useForm({
    _method: 'PUT',
    first_name: props.user.first_name,
    last_name: props.user.last_name,
    email: props.user.email,
    photo: null,
});

const verificationLinkSent = ref(null);
const photoPreview = ref(null);
const photoInput = ref(null);

const updateProfileInformation = () => {
    if (photoInput.value) {
        form.photo = photoInput.value.files[0];
    }

    form.post(route('user-profile-information.update'), {
        errorBag: 'updateProfileInformation',
        preserveScroll: true,
        onSuccess: () => {
            toast.success('Profile information is updated')
            clearPhotoFileInput();
        },
    });
};

const sendEmailVerification = () => {
    verificationLinkSent.value = true;
};

const selectNewPhoto = () => {
    photoInput.value.click();
};

const updatePhotoPreview = () => {
    const photo = photoInput.value.files[0];

    if (! photo) return;

    const reader = new FileReader();

    reader.onload = (e) => {
        photoPreview.value = e.target.result;
    };

    reader.readAsDataURL(photo);
};

const deletePhoto = () => {
    router.delete(route('current-user-photo.destroy'), {
        preserveScroll: true,
        onSuccess: () => {
            photoPreview.value = null;
            clearPhotoFileInput();
        },
    });
};

const clearPhotoFileInput = () => {
    if (photoInput.value?.value) {
        photoInput.value.value = null;
    }
};
</script>

<template>
    <form @submit.prevent="updateProfileInformation">
        <v-row>
            <v-col cols="12" sm="4">
                <div class="text-h6 pb-1">Profile Information</div>
                <div class="text-body-2 text-secondary">Update your account's profile information and email address.</div>
            </v-col>
            <v-col cols="12" sm="8">
                <v-card>
                    <v-card-text>
                        <div class="d-flex flex-column ga-2">
                            <v-row>
                                <v-col cols="12" class="d-flex flex-column ga-4">
                                    <div class="pb-4" v-if="$page.props.jetstream.managesProfilePhotos">
                                        <input
                                            id="photo"
                                            ref="photoInput"
                                            type="file"
                                            class="d-none"
                                            @change="updatePhotoPreview"
                                        >

                                        <!-- <InputLabel for="photo" value="Photo" /> -->

                                        <!-- Current Profile Photo -->
                                        <div v-show="! photoPreview" class="mt-2">
                                            <v-avatar :image="user.profile_photo_url" size="100px" :alt="user.name"></v-avatar>
                                        </div>

                                        <!-- New Profile Photo Preview -->
                                        <div v-show="photoPreview" class="mt-2">
                                            <v-avatar :image="photoPreview" size="100px"></v-avatar>
                                        </div>

                                        <div class="d-flex ga-2 mt-4 flex-wrap">
                                            <v-btn-input-compact color="secondary" variant="outlined" text="Select A New Photo" @click.prevent="selectNewPhoto"></v-btn-input-compact>
                                            <v-btn-input-compact v-if="user.profile_photo_path" color="danger" variant="outlined" text="Remove Photo" @click.prevent="deletePhoto"></v-btn-input-compact>

                                        </div>

                                        <InputError :message="form.errors.photo" class="mt-2" />
                                    </div>
                                    <v-row class="grid-gap grid-gap-3">
                                        <v-col cols="12" sm="6">
                                            <div>
                                                <v-text-field-v1
                                                    v-model="form.first_name"
                                                    label="First Name"
                                                    required
                                                    :error-messages="form.errors.first_name"
                                                ></v-text-field-v1>
                                            </div>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <div>
                                                <v-text-field-v1
                                                    v-model="form.last_name"
                                                    label="Last Name"
                                                    required
                                                    :error-messages="form.errors.last_name"
                                                ></v-text-field-v1>
                                            </div>
                                        </v-col>
                                    </v-row>
                                    
                                    <div>
                                        <v-text-field-v1
                                            v-model="form.email"
                                            label="Email"
                                            required
                                            :error-messages="form.errors.email"
                                        ></v-text-field-v1>
                                    </div>
                                    <div>
                                        <div v-if="$page.props.jetstream.hasEmailVerification && user.email_verified_at === null">
                                            <p class="text-body-2">
                                                Your email address is unverified.

                                                <Link
                                                    :href="route('verification.send')"
                                                    method="post"
                                                    as="button"
                                                    class="text-decoration-underline text-body-1 text-link"
                                                    @click.prevent="sendEmailVerification"
                                                >
                                                    Click here to re-send the verification email.
                                                </Link>
                                            </p>

                                            <div v-show="verificationLinkSent" class="mt-2 text-success">
                                                A new verification link has been sent to your email address.
                                            </div>
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>
                        </div>
                        
                    </v-card-text>
                    <v-card-actions-default>
                        <div class="d-flex justify-space-between align-center flex-grow-1 ga-4">
                            <div>
                                <span class="text-success" v-if="form.recentlySuccessful">
                                    <span class="fa fa-check"></span> Saved
                                </span>
                            </div>
                            <v-btn-input-compact type="submit" color="primary" text="Save" :disabled="form.processing" :loading="form.processing">
                                <template #prepend>
                                    <mdi-icon>check</mdi-icon>
                                </template>
                            </v-btn-input-compact>
                        </div>
                    </v-card-actions-default>
                </v-card>
            </v-col>
        </v-row>
    </form>
    <!-- <FormSection @submitted="updateProfileInformation">
        <template #title>
            Profile Information
        </template>

        <template #description>
            Update your account's profile information and email address.
        </template>

        <template #form>
            
            <div v-if="$page.props.jetstream.managesProfilePhotos" class="col-span-6 sm:col-span-4">
                <input
                    id="photo"
                    ref="photoInput"
                    type="file"
                    class="hidden"
                    @change="updatePhotoPreview"
                >

                <InputLabel for="photo" value="Photo" />

                <div v-show="! photoPreview" class="mt-2">
                    <img :src="user.profile_photo_url" :alt="user.name" class="rounded-full h-20 w-20 object-cover">
                </div>

                <div v-show="photoPreview" class="mt-2">
                    <span
                        class="block rounded-full w-20 h-20 bg-cover bg-no-repeat bg-center"
                        :style="'background-image: url(\'' + photoPreview + '\');'"
                    />
                </div>

                <v-btn color="secondary2" text="Select A New Photo" @click.prevent="selectNewPhoto"></v-btn>
                <SecondaryButton class="" type="button" >
                    Select A New Photo
                </SecondaryButton>

                <SecondaryButton
                    v-if="user.profile_photo_path"
                    type="button"
                    class="mt-2"
                    @click.prevent="deletePhoto"
                >
                    Remove Photo
                </SecondaryButton>

                <InputError :message="form.errors.photo" class="mt-2" />
            </div>

            <div class="col-span-6 sm:col-span-4">
                <InputLabel for="name" value="Name" />
                <TextInput
                    id="name"
                    v-model="form.name"
                    type="text"
                    class="mt-1 block w-full"
                    required
                    autocomplete="name"
                />
                
            </div>

            <div class="col-span-6 sm:col-span-4">
                <InputLabel for="email" value="Email" />
                <TextInput
                    id="email"
                    v-model="form.email"
                    type="email"
                    class="mt-1 block w-full"
                    required
                    autocomplete="username"
                />
                

                <div v-if="$page.props.jetstream.hasEmailVerification && user.email_verified_at === null">
                    <p class="text-sm mt-2">
                        Your email address is unverified.

                        <Link
                            :href="route('verification.send')"
                            method="post"
                            as="button"
                            class="underline text-sm text-gray-600 hover:text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            @click.prevent="sendEmailVerification"
                        >
                            Click here to re-send the verification email.
                        </Link>
                    </p>

                    <div v-show="verificationLinkSent" class="mt-2 font-medium text-sm text-green-600">
                        A new verification link has been sent to your email address.
                    </div>
                </div>
            </div>
        </template>

        <template #actions>
            <ActionMessage :on="form.recentlySuccessful" class="me-3">
                Saved.
            </ActionMessage>

            <PrimaryButton :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                Save
            </PrimaryButton>
        </template>
    </FormSection> -->
</template>
